import { Column } from "../../../../../core/components/Column";
import { TextComponent } from "../../../../../core/components/TextComponent";
import { CustomButton } from "../../../../../core/components/CustomButton";
import { Select } from "../../../../../core/components/Select";
import { ErrorMessage, Field, Form, Formik, FormikHelpers } from "formik";
import { SelectDetailsFormValues, selectdetailsValidationSchema } from "../../interfaces/SelectFormInterface";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../../core/store/store";
import { setProfileConfig, setUserInfo } from "../../store/OnboardingSlice";
import Category from "../../../domain/entities/category";
import { useEffect, useMemo, useState } from "react";

const isPrimaryGenderOption = (gender: Category) => {
    const name = gender.name.trim().toLowerCase();
    return name === "man" || name === "male" || name === "woman" || name === "female";
};

const genderLabel = (gender: Category) => {
    const name = gender.name.trim().toLowerCase();
    if (name === "man" || name === "male") return "MALE";
    if (name === "woman" || name === "female") return "FEMALE";
    return gender.name.toUpperCase();
};

export const SelectDetails = () => {
    const navigate = useNavigate();
    const dispatch: AppDispatch = useDispatch();
    const { user, userInfo, profileConfig, getProfileConfig, showMeList, desiresList, sexualOrientationList, genderList, maritialList } = useSelector((state: RootState) => state.onboarding);
    const [showMoreGenderOptions, setShowMoreGenderOptions] = useState(false);

    const initialValues: SelectDetailsFormValues = {
        gender: userInfo?.gender_id || user?.gender_id || "",
        sexualOrientation: userInfo?.sexual_orientation_id || user?.sexual_orientation_id || "",
        lookingFor:
            profileConfig?.desires ||
            getProfileConfig?.user_has_desire?.map((item) => item.desire_id) ||
            [],
        status: userInfo?.marital_status_id || user?.marital_status_id || "",
        showMe:
            profileConfig?.showme ||
            getProfileConfig?.user_has_showme?.map((item) => item.showme_id) ||
            [],
    };

    const handleSubmit = (values: SelectDetailsFormValues, { setSubmitting }: FormikHelpers<SelectDetailsFormValues>) => {
        dispatch(setUserInfo({
            gender_id: values.gender,
            sexual_orientation_id: values.sexualOrientation,
            marital_status_id: values.status
        }));

        dispatch(setProfileConfig({
            desires: values.lookingFor,
            showme: values.showMe
        }));

        navigate("/location");
        setSubmitting(false);
    }

    const primaryGenderOptions = useMemo(() => {
        const primaryOptions = genderList.filter(isPrimaryGenderOption);
        return primaryOptions.length > 0 ? primaryOptions : genderList.slice(0, 2);
    }, [genderList]);

    const visibleGenderOptions = showMoreGenderOptions ? genderList : primaryGenderOptions;
    const showGenderToggle = genderList.length > primaryGenderOptions.length;

    useEffect(() => {
        const selectedGender = genderList.find((gender) => gender.id === initialValues.gender);
        if (selectedGender && !isPrimaryGenderOption(selectedGender)) {
            setShowMoreGenderOptions(true);
        }
    }, [genderList, initialValues.gender]);

    return (
        <Column
            crossAxisAlignment="center"
            className="md:bg-white px-10 pb-10 md:my-10 md:p-20 self-center text-center w-full md:w-[584px] lg:shadow-xl rounded-lg"
        >
            <TextComponent variant="h2" className="font-bold">
                Select Details
            </TextComponent>
            <TextComponent variant="h6" className="text-[#6B6B6B] mt-2">
                Please select below details to continue
            </TextComponent>
            <Formik
                initialValues={initialValues}
                validationSchema={selectdetailsValidationSchema}
                validateOnBlur
                validateOnChange={false}
                onSubmit={handleSubmit}
            >
                {({ handleChange, handleBlur, values }) => {
                    const handleGenderSelect = (genderId: string) => {
                        handleChange({
                            target: {
                                name: "gender",
                                value: genderId,
                            },
                        } as React.ChangeEvent<any>);
                        handleBlur({
                            target: {
                                name: "gender",
                            },
                        } as React.FocusEvent<any>);
                    };

                    return (
                    <Column className="py-2 md:py-4 gap-2 w-full md:w-[363px]">
                        <Form className="flex flex-col mt-5 text-start gap-2 md:gap-1">
                            <Field type="hidden" id="gender" name="gender" />
                            <Column className="text-start gap-2">
                                <TextComponent variant="h6">
                                    Select your Gender <span className="text-red-500 select-none">*</span>
                                </TextComponent>
                                <div className="flex flex-col gap-2">
                                    {visibleGenderOptions.map((gender) => {
                                        const isSelected = values.gender === gender.id;
                                        return (
                                            <button
                                                key={gender.id}
                                                type="button"
                                                onClick={() => handleGenderSelect(gender.id)}
                                                aria-pressed={isSelected}
                                                className={`flex w-full items-center justify-between rounded border px-2.5 py-2.5 text-left transition-colors duration-300 ease-in-out md:px-5 md:py-3 ${
                                                    isSelected
                                                        ? "border-primary bg-[#FFF1FA]"
                                                        : "border-gray-300 bg-white hover:bg-grayDefault"
                                                }`}
                                            >
                                                <TextComponent variant="h5" className="uppercase">
                                                    {genderLabel(gender)}
                                                </TextComponent>
                                                <span
                                                    className={`h-4 w-4 rounded-full border-2 ${
                                                        isSelected ? "border-primary bg-primary" : "border-[#CFCFCF] bg-white"
                                                    }`}
                                                />
                                            </button>
                                        );
                                    })}
                                    {showGenderToggle && (
                                        <button
                                            type="button"
                                            onClick={() => setShowMoreGenderOptions((currentValue) => !currentValue)}
                                            className="flex w-full items-center justify-between rounded border border-gray-300 bg-white px-2.5 py-2.5 text-left transition-colors duration-300 ease-in-out hover:bg-grayDefault md:px-5 md:py-3"
                                        >
                                            <TextComponent variant="h5" className="uppercase">
                                                {showMoreGenderOptions ? "LESS" : "MORE"}
                                            </TextComponent>
                                        </button>
                                    )}
                                </div>
                            </Column>
                            <ErrorMessage name="gender" component="div" className="text-red-500 text-xs" />
                            <Field
                                as={Select}
                                id="sexualOrientation"
                                name="sexualOrientation"
                                label="Sexual Orientation"
                                items={sexualOrientationList.map((e) => ({ key: e.id, label: e.name }))}
                                defaultSelectedKeys={[initialValues.sexualOrientation]}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                            />
                            <ErrorMessage name="sexualOrientation" component="div" className="text-red-500 text-xs" />
                            <Field
                                as={Select}
                                id="lookingFor"
                                name="lookingFor"
                                selectionMode="multiple"
                                label="I am Looking For"
                                items={desiresList.map((e) => ({ key: e.id, label: e.name }))}
                                defaultSelectedKeys={initialValues.lookingFor}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                            />
                            <ErrorMessage name="lookingFor" component="div" className="text-red-500 text-xs" />
                            <Field
                                as={Select}
                                id="status"
                                name="status"
                                label="My Status is"
                                items={maritialList.map((e) => ({ key: e.id, label: e.name }))}
                                defaultSelectedKeys={[initialValues.status]}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                            />
                            <ErrorMessage name="status" component="div" className="text-red-500 text-xs" />
                            <Field
                                as={Select}
                                id="showMe"
                                name="showMe"
                                selectionMode="multiple"
                                label="Show Me"
                                items={showMeList.map((e) => ({ key: e.id, label: e.name }))}
                                defaultSelectedKeys={initialValues.showMe}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                            />
                            <ErrorMessage name="showMe" component="div" className="text-red-500 text-xs" />
                            <CustomButton type="submit" className="mt-4">
                                Continue
                            </CustomButton>
                        </Form>
                    </Column>
                    );
                }}
            </Formik>
        </Column>
    );
};
